import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarCheck2, PanelLeftClose, PanelLeftOpen, Users } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  ApiError,
  clearChatLog,
  getTokenUsage,
  confirmCohortIntent,
  dropCohortIntent,
  postTurn,
  proposeCohortIntent,
  searchMentionCandidates,
  listAvailabilityPatterns,
} from "@/api/client";
import { AgentInfo, ClarificationKind, CohortBatchView, ClarificationOption, ConfirmationMode, DomainEvent, MeetingInterpretation, NegotiationStatus, ParticipantState, TokenUsageView, UserEvent } from "@/api/types";
import { AskedForTimePanel } from "@/features/agentrequests/AskedForTimePanel";
import { MeetingsAtAGlance } from "@/features/meetings/MeetingsAtAGlance";
import { formatSlotRange } from "@/lib/datetime";

import { bookedOutcomeText, noAgreementOutcomeText } from "./components/a2aSlots";
import { PageTitle } from "@/components/PageTitle";
import { CalendarsCard } from "./components/CalendarsCard";
import { ConversationDetailDrawer } from "./components/ConversationDetailDrawer";
import { ConversationPanel, type SigilCandidate } from "./components/ConversationPanel";
import { type SigilKind } from "./sigils";
import { TokenUsageBadge } from "./components/TokenUsageBadge";
import { PlatformConnectionsPanel } from "./components/PlatformConnectionsPanel";
import { ToastStack, useToasts } from "./components/ToastStack";
import { TransmissionPanel } from "./components/TransmissionPanel";
import { TransmissionMode, WireParticipant } from "./components/types";
import { useChatHistory } from "./components/useChatHistory";
import { useTransmissionActivity } from "./components/useTransmissionActivity";
import { usePolling } from "./components/usePolling";
import { extractHandles } from "./mentions";

/** Prefer @handle when known; fall back through displayName, then a short id stub. */
function labelFor(agent: AgentInfo): string {
  if (agent.handle) return `@${agent.handle}`;
  if (agent.displayName) return agent.displayName;
  return agent.id.slice(0, 8);
}

/** Per-conversation snapshot shown on the Incoming wire (and tracked for the activity cursor). */
type IncomingConv = {
  conversationId: string;
  status: NegotiationStatus;
  initiator: AgentInfo;
  lastActivityAt: string;
};

/** localStorage cursor for the Incoming-toggle activity dot. */
const LAST_SEEN_KEY = "a2a:incoming:lastSeen";

export function ChatPage() {
  const [text, setText] = React.useState("");
  // Agent-card CTA deep link: /?to=handle prefills the composer with the
  // @mention so the visitor lands ready to type the request. Consumed once.
  const [searchParams, setSearchParams] = useSearchParams();
  React.useEffect(() => {
    const to = searchParams.get("to");
    if (!to) return;
    setText((current) => (current === "" ? `@${to.replace(/^@/, "")} ` : current));
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only consume
  }, []);
  const [optionalHandles, setOptionalHandles] = React.useState<Set<string>>(
    new Set(),
  );
  const [clarifyOptions, setClarifyOptions] = React.useState<ClarificationOption[]>([]);
  // Which input widget the pending clarification wants, and the parsed context to
  // show as a card alongside it (on a confirmation).
  const [clarifyKind, setClarifyKind] = React.useState<ClarificationKind>("choice");
  const [interpretation, setInterpretation] = React.useState<MeetingInterpretation | null>(null);
  // Confirm-before-booking toggle, persisted so the choice survives reloads.
  const [confirmMode, setConfirmMode] = React.useState<ConfirmationMode>(() => {
    try {
      return localStorage.getItem("a2a.confirmMode") === "pre_confirm" ? "pre_confirm" : "auto";
    } catch {
      return "auto";
    }
  });
  const changeConfirmMode = React.useCallback((next: ConfirmationMode) => {
    setConfirmMode(next);
    try {
      localStorage.setItem("a2a.confirmMode", next);
    } catch {
      /* ignore storage failures (private mode, etc.) */
    }
  }, []);
  const [busy, setBusy] = React.useState(false);
  const [conversationId, setConversationId] = React.useState<string | null>(null);
  // Set while an availability-pattern clarification is in flight: the draft id we echo
  // on the next message so the backend continues the same request. Distinct
  // from `conversationId` (a real negotiation we subscribe to over SSE).
  const [pendingDraftConvId, setPendingDraftConvId] = React.useState<string | null>(null);
  const [participants, setParticipants] = React.useState<WireParticipant[]>([]);
  const [streamErr, setStreamErr] = React.useState(false);
  // The main chat is always the user's own thread; the transmission lanes now
  // convey direction, so there's no manual outgoing/incoming toggle.
  const mode: TransmissionMode = "outgoing";
  // The left workspace rail (calendars · wire · connections · meetings) collapses
  // to a thin icon strip so the chat can take the full width. Persisted.
  const [railOpen, setRailOpen] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem("a2a:home:rail") !== "0";
    } catch {
      return true;
    }
  });
  const toggleRail = React.useCallback(() => {
    setRailOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("a2a:home:rail", next ? "1" : "0");
      } catch {
        /* ignore storage failures (private mode, etc.) */
      }
      return next;
    });
  }, []);
  const [drawerConvId, setDrawerConvId] = React.useState<string | null>(null);
  const [incoming, setIncoming] = React.useState<Map<string, IncomingConv>>(new Map());
  const [convEvents, setConvEvents] = React.useState<Map<string, UserEvent[]>>(new Map());
  const [currentRound, setCurrentRound] = React.useState<number | null>(null);
  // Bumped on each accepted booking so the "Booked with" panel refetches.
  const [relationshipsRefreshKey, setRelationshipsRefreshKey] = React.useState(0);
  const [lastSeenAt, setLastSeenAt] = React.useState<string | null>(() =>
    typeof window === "undefined" ? null : window.localStorage.getItem(LAST_SEEN_KEY),
  );
  const { messages, append, reset, replaceProposal } = useChatHistory();

  /** The intent whose button was just pressed, so only that card's controls go quiet. */
  const [busyIntentId, setBusyIntentId] = React.useState<string | null>(null);
  const { toasts, push: pushToast, dismiss: dismissToast } = useToasts();
  // The user's own token consumption, shown in the chat footer. Refreshed on
  // mount and after each turn (a turn spends tokens, or is blocked at the cap).
  const [tokenUsage, setTokenUsage] = React.useState<TokenUsageView | null>(null);
  const refreshTokenUsage = React.useCallback(() => {
    getTokenUsage()
      .then(setTokenUsage)
      .catch(() => {
        /* non-fatal — the indicator just won't update this tick */
      });
  }, []);
  React.useEffect(() => {
    refreshTokenUsage();
  }, [refreshTokenUsage]);
  // Direction-aware activity for the transmission lanes (each lingers ~2s past
  // its last event). Fed from the SSE handler + the local submit below.
  const { outActive, inActive, triggerOut, triggerIn } = useTransmissionActivity();

  // A single poll loop per tab (usePolling) diffs REST snapshots into the same
  // UserEvent stream the SSE feed used to push. Fans out into incoming/conv-
  // events state and (when the event matches the currently-active conversation)
  // into the outgoing wire + outcome bubbles.
  const activeConvIdRef = React.useRef<string | null>(null);
  activeConvIdRef.current = conversationId;
  // Whether the poller has seeded its baseline snapshot (the `caught_up`
  // boundary). The first poll rebuilds existing state silently — the chat log
  // already carries it — so we skip toasts/bubbles until then. After it, diffs
  // are genuinely new and get narrated. The poller always signals this after
  // its first successful poll, so there's no fallback timer to race the seed.
  const caughtUpRef = React.useRef(false);
  // Conversations we've already opened an "incoming" narration block for, so a
  // re-emitted ConversationInvited (reconnect / re-seed) never duplicates the
  // divider + "negotiating on your behalf" bubble. One block per conversation.
  const narratedInvitesRef = React.useRef(new Set<string>());
  // Conversations someone else initiated → their initiator's label, so the
  // terminal handler can phrase the outcome as "accepted on your behalf".
  const incomingLabelsRef = React.useRef(new Map<string, string>());

  const { pokeNow } = usePolling(
    React.useCallback(
      (event: UserEvent, _seq: number) => {
        const convId = event.data.conversationId;
        const now = new Date().toISOString();
        // Buffered-replay events (before `caught_up`) rebuild live state silently;
        // only events after the boundary get bubbles + toasts.
        const isNew = caughtUpRef.current;

        // 0. Light the transmission lanes by direction. Only genuinely-new
        //    events animate — replays on reconnect rebuild state silently.
        if (isNew) {
          if (event.type === "NegotiationOpened" || event.type === "RoundAdvanced") {
            triggerOut();
          } else if (
            event.type === "ConversationInvited" ||
            event.type === "CounterReceived" ||
            event.type === "SlotAccepted" ||
            event.type === "NegotiationFailed"
          ) {
            triggerIn();
          }
        }

        // 1. Maintain the per-conversation incoming snapshot (drives Incoming wire).
        setIncoming((prev) => {
          const next = new Map(prev);
          if (event.type === "ConversationInvited") {
            next.set(convId, {
              conversationId: convId,
              status: "OPEN",
              initiator: event.data.initiator,
              lastActivityAt: now,
            });
          } else {
            const existing = next.get(convId);
            if (existing) {
              next.set(convId, {
                ...existing,
                status: statusAfter(existing.status, event),
                lastActivityAt: now,
              });
            }
            // Activity for a conv I initiated never enters the Incoming map.
          }
          return next;
        });

        // 2. Append to per-conv event log so the drawer can render live.
        setConvEvents((prev) => {
          const next = new Map(prev);
          const arr = next.get(convId) ?? [];
          next.set(convId, [...arr, event]);
          return next;
        });

        // 3. If this event belongs to the currently-active outgoing conversation,
        //    update wire participants + system narration + round badge.
        if (event.type !== "ConversationInvited" && convId === activeConvIdRef.current) {
          applyEventToParticipants(event, setParticipants);
          if (event.type === "RoundAdvanced") setCurrentRound(event.data.round);
          const msg = describeEvent(event);
          if (msg) append({ from: "system", text: msg, conversationId: convId });
        }

        // 4. An invitation opens an inbound block in the main chat: the agent
        //    negotiates it autonomously, so narrate it where it happened.
        //    (The label is tracked even for replays so terminal phrasing below
        //    can still say "on your behalf"; only the bubbles are gated.)
        if (event.type === "ConversationInvited") {
          const who = labelFor(event.data.initiator);
          incomingLabelsRef.current.set(convId, who);
          if (isNew && !narratedInvitesRef.current.has(convId)) {
            narratedInvitesRef.current.add(convId);
            append({
              from: "divider",
              kind: "start-incoming",
              label: `incoming · ${who}`,
              conversationId: convId,
            });
            append({
              from: "agent",
              text: `📨 ${who} is requesting a meeting, negotiating on your behalf…`,
              conversationId: convId,
            });
          }
        }

        // 5. Terminal outcomes close their block — outcome bubble + a divider
        //    carrying the booked start–end — for EVERY conversation (active
        //    outgoing, stale outgoing, or auto-accepted inbound). Plus toast.
        //    Suppressed for replays: chat-log hydration already carries them.
        if (isNew && (event.type === "SlotAccepted" || event.type === "NegotiationFailed")) {
          const incomingWho = incomingLabelsRef.current.get(convId) ?? null;
          // Same shared phrasing the refresh-hydration path uses, so the bubble
          // reads identically live and after a reload.
          const outcome =
            event.type === "SlotAccepted"
              ? bookedOutcomeText(
                  formatSlotRange(event.data.slot.start, event.data.slot.end),
                  incomingWho,
                )
              : noAgreementOutcomeText(incomingWho, event.data.reason);
          if (outcome) append({ from: "agent", text: outcome, conversationId: convId });
          append({
            from: "divider",
            kind: "end",
            label: endLabelFor(event),
            conversationId: convId,
          });
        }
        if (isNew && event.type === "SlotAccepted") {
          // A booking just landed — the pair's remembered type may have changed.
          setRelationshipsRefreshKey((k) => k + 1);
          pushToast({
            tone: "success",
            title: "Agreement reached",
            body: `Locked in ${formatSlotRange(event.data.slot.start, event.data.slot.end)}.`,
          });
        } else if (isNew && event.type === "NegotiationFailed") {
          pushToast({
            tone: "failure",
            title: "Couldn't reach agreement",
            body: event.data.reason,
          });
        }
      },
      [append, pushToast, triggerOut, triggerIn],
    ),
    // err on sustained poll failure, null on recovery — drives the footer hint.
    React.useCallback((err: unknown) => setStreamErr(Boolean(err)), []),
    // The poller seeded its baseline — narrate everything from here on.
    React.useCallback(() => {
      caughtUpRef.current = true;
    }, []),
    conversationId,
  );

  async function send(messageText: string, selectedOptionValue?: string) {
    const trimmed = messageText.trim();
    if (!trimmed || busy) return;
    append({ from: "you", text: trimmed });
    setText("");
    setOptionalHandles(new Set());
    setClarifyOptions([]); // a sent message answers any pending clarification
    setClarifyKind("choice");
    setInterpretation(null);
    setBusy(true);

    // ONE path. The browser decides nothing about the sentence (design 5.1): the server routes
    // it — a still-sticky proposal folds it in as a refinement, a closed .{ } selects the cohort
    // interpreter, anything else reaches the ordinary parser — and `channel` says what happened.
    try {
      const mentioned = extractHandles(trimmed).map((handle) => ({
        handle,
        required: !optionalHandles.has(handle),
      }));
      const turn = await postTurn({
        text: trimmed,
        // Continue an in-flight clarification by echoing its draft id.
        conversation_id: pendingDraftConvId ?? undefined,
        // @handle tokens drive backend resolution by handle; omit when none.
        participants: mentioned.length > 0 ? mentioned : undefined,
        // Set when the user clicked a quick-reply: the server applies the
        // option's value (e.g. an encoded time window) deterministically.
        selected_option_value: selectedOptionValue,
        // The confirm-before-booking toggle: pre_confirm gates before fan-out.
        confirmation_mode: confirmMode,
      });
      if (turn.channel === "cohort") {
        // A proposal (or a question about one). Acts on nothing until a card is confirmed.
        if (turn.batch.question) {
          append({ from: "agent", text: turn.batch.question, conversationId: null });
        }
        if (turn.batch.id) {
          showBatch(turn.batch);
        }
        return; // the finally below still releases the composer
      }
      const res = turn.chat;
      triggerOut(); // light the OUT lane as the agent reaches out
      const opened = res.status === "ok" && !!res.conversation_id;
      const clarifying =
        res.status === "needs_clarification" && !!res.conversation_id;
      append({
        from: "agent",
        text: res.reply,
        // Only link the bubble to a real negotiation, never to a draft id.
        conversationId: opened ? res.conversation_id : null,
      });
      if (opened) {
        setStreamErr(false);
        setParticipants([]);
        setCurrentRound(null);
        setConversationId(res.conversation_id);
        setPendingDraftConvId(null);
        // Don't wait up to a full poll interval to light the wire — poll now.
        pokeNow();
      } else if (clarifying) {
        // Keep threading the loop on the next message; offer the choices as
        // quick-reply buttons (or a typed input) so the user can answer.
        setPendingDraftConvId(res.conversation_id);
        setClarifyOptions(res.clarification_options ?? []);
        setClarifyKind(res.clarification_kind ?? "choice");
        setInterpretation(res.interpretation ?? null);
      } else {
        // Unsupported / gave up — drop any pending draft so we start fresh.
        setPendingDraftConvId(null);
      }
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `Request failed (${err.status}): ${err.body || err.message}`
          : `Request failed: ${err}`;
      append({ from: "system", text: msg });
    } finally {
      setBusy(false);
      // The turn spent tokens (or was blocked at the cap) — refresh the badge.
      refreshTokenUsage();
    }
  }

  /**
   * Put a batch on screen, replacing any earlier rendering of the same one.
   *
   * <p>Replaced rather than appended, so refining three times leaves one set of cards rather than
   * three, and the struck-through rows on it are this turn's delta rather than an accumulating
   * history of every turn.</p>
   */
  function showBatch(batch: CohortBatchView) {
    // Nothing else to do: this is purely rendering. Whether the next typed message is a
    // refinement is the server's decision (TurnRouter, off the table), not read back off this.
    replaceProposal(batch);
  }

  async function confirmIntent(batchId: string, intentId: string) {
    setBusyIntentId(intentId);
    try {
      showBatch(
        await confirmCohortIntent(batchId, intentId, Intl.DateTimeFormat().resolvedOptions().timeZone),
      );
      // A confirmed reschedule opens negotiations, so light the wire without waiting a poll.
      pokeNow();
    } catch (err) {
      append({ from: "system", text: describeError(err) });
    } finally {
      setBusyIntentId(null);
    }
  }

  async function dropIntent(batchId: string, intentId: string) {
    setBusyIntentId(intentId);
    try {
      showBatch(await dropCohortIntent(batchId, intentId));
    } catch (err) {
      append({ from: "system", text: describeError(err) });
    } finally {
      setBusyIntentId(null);
    }
  }

  /**
   * A quick-reply answer to one card's question — the pattern labels, mostly. The same wire
   * shape as typing it (intent_id scopes the reply to that card), so the transcript shows the
   * click as the words it stood for.
   */
  async function answerIntent(batchId: string, intentId: string, answer: string) {
    append({ from: "you", text: answer });
    setBusyIntentId(intentId);
    try {
      showBatch(
        await proposeCohortIntent(
          answer,
          Intl.DateTimeFormat().resolvedOptions().timeZone,
          batchId,
          intentId,
        ),
      );
    } catch (err) {
      append({ from: "system", text: describeError(err) });
    } finally {
      setBusyIntentId(null);
      refreshTokenUsage();
    }
  }

  function describeError(err: unknown): string {
    return err instanceof ApiError
      ? `Request failed (${err.status}): ${err.body || err.message}`
      : `Request failed: ${err}`;
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    void send(text);
  }

  // Start a fresh thread: advance the server watermark (so a reload stays
  // clean), empty the transcript, and drop the active outgoing-conversation
  // state so the next message is unambiguously new. The Incoming wire is live
  // status and intentionally left intact.
  async function newThread() {
    try {
      await clearChatLog();
    } catch {
      // Non-fatal — clear the local view anyway; the watermark just won't
      // persist across reloads this time.
    }
    reset();
    setText("");
    setConversationId(null);
    setPendingDraftConvId(null);
    setParticipants([]);
    setCurrentRound(null);
    setClarifyOptions([]);
    setStreamErr(false);
  }

  // Derive Incoming wire participants from the live incoming map.
  const incomingParticipants = React.useMemo<WireParticipant[]>(() => {
    return Array.from(incoming.values()).map((c) => ({
      id: c.initiator.id,
      label: labelFor(c.initiator),
      state: statusToParticipantState(c.status),
    }));
  }, [incoming]);

  // Max last-activity timestamp across all incoming convs — drives the RX dot.
  const maxIncomingActivity = React.useMemo(() => {
    let max: string | null = null;
    for (const c of incoming.values()) {
      if (max == null || c.lastActivityAt > max) max = c.lastActivityAt;
    }
    return max;
  }, [incoming]);

  const hasUnseenActivity =
    maxIncomingActivity != null &&
    (lastSeenAt == null || maxIncomingActivity > lastSeenAt);

  function markIncomingSeen() {
    if (maxIncomingActivity == null) return;
    window.localStorage.setItem(LAST_SEEN_KEY, maxIncomingActivity);
    setLastSeenAt(maxIncomingActivity);
  }

  // Who's on the wire — the active outgoing peers, else any inbound caller.
  const peerLabel = React.useMemo<string | null>(() => {
    const pick = participants.length > 0 ? participants : incomingParticipants;
    if (pick.length === 0) return null;
    if (pick.length === 1) return pick[0].label;
    return `${pick.length} agents`;
  }, [participants, incomingParticipants]);

  return (
    <div className="mx-auto max-w-[1140px] space-y-4 px-4 py-6 font-comic-body">
      <header>
        <PageTitle>Schedule through your agent</PageTitle>
        <p className="text-xs text-muted-foreground">
          Describe it in plain English. Try{" "}
          <em>"find 30 minutes with @priya tomorrow morning"</em>, or{" "}
          <em>"move my meeting with @sam to Friday"</em>.
        </p>
      </header>

      {/* Three columns: your workspace (calendars + connections) · the chat,
          front and centre · the live A2A wire and upcoming meetings on the
          right, where scheduling clients put the agenda. The left rail
          collapses to a thin icon strip so the chat can breathe. */}
      <div
        className={cn(
          "grid items-start gap-4",
          railOpen
            ? "lg:grid-cols-[240px_minmax(0,1fr)_280px]"
            : "lg:grid-cols-[52px_minmax(0,1fr)_280px]",
        )}
      >
        {/* LEFT RAIL — calendars + platform connections. */}
        <aside className={railOpen ? "block" : "block lg:hidden"}>
          <div className="space-y-4">
            <div className="flex items-center justify-between px-1">
              <span className="font-display text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Your workspace
              </span>
              <button
                type="button"
                onClick={toggleRail}
                title="Collapse panel"
                aria-label="Collapse workspace panel"
                className="hidden h-7 w-7 place-items-center rounded-full text-muted-foreground transition hover:bg-muted hover:text-foreground lg:grid"
              >
                <PanelLeftClose className="h-4 w-4" />
              </button>
            </div>
            <CalendarsCard />
            <PlatformConnectionsPanel refreshKey={relationshipsRefreshKey} />
          </div>
        </aside>

        {/* Collapsed left rail: a thin icon strip that expands on click (lg only). */}
        <aside className={railOpen ? "hidden" : "hidden lg:block"}>
          <button
            type="button"
            onClick={toggleRail}
            title="Show your workspace"
            aria-label="Show your workspace panel"
            className="group flex w-14 flex-col items-center gap-4 rounded-3xl bg-muted/30 py-4 transition hover:bg-muted/50"
          >
            <PanelLeftOpen className="icon-live h-5 w-5 text-muted-foreground group-hover:text-foreground" />
            <span className="h-px w-6 bg-border" />
            <CalendarCheck2 className="icon-breathe h-5 w-5 text-brand-teal" />
            <Users className="icon-live h-5 w-5 text-brand-blue" />
          </button>
        </aside>

        {/* CENTER — the chat. (The onboarding goal-gradient lives solely on the
            profile/setup page now, so there's one checklist, not two.) */}
        <div className="min-w-0 space-y-4">
          <ConversationPanel
          mode={mode}
          messages={messages}
          draft={text}
          onDraftChange={setText}
          onSubmit={onSubmit}
          busy={busy}
          optionalHandles={optionalHandles}
          onToggleOptional={(handle) =>
            setOptionalHandles((prev) => {
              const next = new Set(prev);
              if (next.has(handle)) next.delete(handle);
              else next.add(handle);
              return next;
            })
          }
          onMentionSearch={(q) => searchMentionCandidates(q)}
          onSigilSearch={searchSigilCandidates}
          quickReplies={clarifyOptions}
          onQuickReply={(option) => void send(option.label, option.value)}
          clarifyKind={clarifyKind}
          interpretation={interpretation}
          confirmMode={confirmMode}
          onConfirmModeChange={changeConfirmMode}
          onNewThread={() => void newThread()}
          onOpenConversation={setDrawerConvId}
          onConfirmIntent={(batchId, intentId) => void confirmIntent(batchId, intentId)}
          onDropIntent={(batchId, intentId) => void dropIntent(batchId, intentId)}
          onAnswerIntent={(batchId, intentId, answer) =>
            void answerIntent(batchId, intentId, answer)
          }
          busyIntentId={busyIntentId}
          emptyHint={
            <span>
              No messages yet. Try{" "}
              <em className="text-foreground">"find 30 minutes with @priya this week"</em>.{" "}
              Your agent handles the back-and-forth and you just approve the result.
            </span>
          }
          footer={
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
              <span className="text-xs text-muted-foreground">
                {conversationId && (
                  <>
                    Conversation <code>{conversationId.slice(0, 8)}…</code>
                    {streamErr && (
                      <span className="ml-2 text-amber-700">
                        · updates delayed, retrying
                      </span>
                    )}
                  </>
                )}
              </span>
              <TokenUsageBadge usage={tokenUsage} />
            </div>
          }
          />
        </div>

        {/* RIGHT RAIL — the live A2A wire (only meaningful next to the chat)
            and the upcoming-meetings agenda, the conventional spot for it. */}
        <aside className="space-y-4">
          <TransmissionPanel
            outActive={outActive}
            inActive={inActive}
            peerLabel={peerLabel}
            incomingPending={hasUnseenActivity}
            onIncomingSeen={markIncomingSeen}
            thinking={busy}
            currentRound={currentRound}
          />
          <AskedForTimePanel />
          <MeetingsAtAGlance />
        </aside>
      </div>

      <ConversationDetailDrawer
        conversationId={drawerConvId}
        liveEvents={drawerConvId ? convEvents.get(drawerConvId) ?? [] : []}
        onClose={() => setDrawerConvId(null)}
      />

      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}


function statusAfter(prev: NegotiationStatus, e: UserEvent): NegotiationStatus {
  if (e.type === "SlotAccepted") return "ACCEPTED";
  if (e.type === "NegotiationFailed") return "FAILED";
  return prev;
}

function statusToParticipantState(s: NegotiationStatus): ParticipantState {
  switch (s) {
    case "ACCEPTED":
      return "ACCEPTED";
    case "FAILED":
    case "EXPIRED":
      return "REJECTED";
    case "OPEN":
    default:
      return "PENDING";
  }
}

/** Closing-divider label for a terminal event: the meeting's start–end, or a no-agreement note. */
function endLabelFor(event: DomainEvent): string {
  if (event.type === "SlotAccepted") {
    return `booked ${formatSlotRange(event.data.slot.start, event.data.slot.end)}`;
  }
  return "no agreement";
}

function describeEvent(event: DomainEvent): string | null {
  switch (event.type) {
    case "NegotiationOpened": {
      const labels = event.data.participants.map(labelFor).join(", ");
      const n = event.data.participants.length;
      return `${n} participant${n === 1 ? "" : "s"} on the wire${labels ? ": " + labels : ""}.`;
    }
    case "RoundAdvanced":
      return `Round ${event.data.round} starting.`;
    case "CounterReceived": {
      // The poller can't always recover the offered-slot count from a snapshot;
      // phrase without a number when it's unknown (empty).
      const n = event.data.slots.length;
      return n === 0 ? "Counter received." : `Counter received: ${n} slot${n === 1 ? "" : "s"}.`;
    }
    case "SlotAccepted":
      return `Slot accepted: ${formatSlotRange(event.data.slot.start, event.data.slot.end)}`;
    case "NegotiationFailed":
      return `Failed: ${event.data.reason}`;
    default:
      return null;
  }
}

function applyEventToParticipants(
  event: DomainEvent,
  setParticipants: React.Dispatch<React.SetStateAction<WireParticipant[]>>,
) {
  switch (event.type) {
    case "NegotiationOpened": {
      setParticipants(
        event.data.participants.map((agent) => ({
          id: agent.id,
          label: labelFor(agent),
          state: "PENDING" as ParticipantState,
        })),
      );
      break;
    }
    case "CounterReceived": {
      const fromId = event.data.from.id;
      setParticipants((prev) =>
        prev.map((p) =>
          p.id === fromId
            ? { ...p, label: labelFor(event.data.from), state: "COUNTERED" }
            : p,
        ),
      );
      break;
    }
    case "SlotAccepted":
      setParticipants((prev) =>
        prev.map((p) => ({ ...p, state: "ACCEPTED" as ParticipantState })),
      );
      break;
    case "NegotiationFailed":
      setParticipants((prev) =>
        prev.map((p) => ({ ...p, state: "REJECTED" as ParticipantState })),
      );
      break;
  }
}

/**
 * What a settled sigil type can name, filtered by what has been typed so far.
 *
 * Patterns are the caller's own and small — a handful, made by hand — so the list is fetched
 * whole and filtered here rather than needing a search endpoint. Departments and roles parse
 * but have no directory behind them yet, so they name nothing and the menu stays empty.
 */
async function searchSigilCandidates(kind: SigilKind, q: string): Promise<SigilCandidate[]> {
  if (kind !== "pattern") {
    return [];
  }
  const query = q.trim().toLowerCase();
  const patterns = await listAvailabilityPatterns();
  return patterns
    .filter((pattern) => !query || pattern.name.toLowerCase().includes(query))
    .map((pattern) => ({
      name: pattern.name,
      detail: pattern.is_default ? "Default" : `${pattern.ranges.length} ranges`,
    }));
}
